import { useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PropertyFilters from "@/components/PropertyFilters";
import PropertyListingCard from "@/components/PropertyListingCard";
import { useLocations } from "@/hooks/useLocations";
import { useProperties } from "@/hooks/useProperties";
import { applyPropertySearchFilters, sanitizePropertySearchFilters, type PropertySearchFilters } from "@/lib/propertyFilters";

const defaultRentFilters: PropertySearchFilters = {
  listingType: "rent",
  category: "furnished",
  locationId: "",
  bedrooms: "",
  maxPrice: "",
};

const PropertiesForRentPage = () => {
  const [filters, setFilters] = useState<PropertySearchFilters>(defaultRentFilters);
  const { data: rentProperties, isLoading } = useProperties({ listing_type: "rent" });
  const { data: locations = [], isLoading: loadingLocations } = useLocations();
  const furnishedCount = useMemo(() => rentProperties?.filter((property) => property.category === "furnished").length ?? 0, [rentProperties]);
  const unfurnishedCount = useMemo(() => rentProperties?.filter((property) => property.category === "unfurnished").length ?? 0, [rentProperties]);
  const filteredProperties = useMemo(() => applyPropertySearchFilters(rentProperties ?? [], filters), [rentProperties, filters]);

  return (
    <div className="bg-brand-canvas min-h-screen">
      <Header />
      <main className="pt-36 md:pt-40 w-full">
        <div className="px-4 md:px-16 py-6">
          <nav className="flex items-center gap-2 text-sm text-primary-foreground/70 flex-wrap">
            <a href="/" className="hover:text-primary-foreground transition-colors">Home</a>
            <span>/</span><span>Properties for Rent</span>
          </nav>
        </div>

        <div className="px-4 md:px-16 py-8 md:py-12">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="font-display text-3xl md:text-5xl font-light text-primary-foreground mb-4 md:mb-6">All Properties for Rent</h1>
            <p className="font-body text-base md:text-lg text-primary-foreground/75 max-w-3xl mx-auto px-4">
              Browse our complete collection of rental properties in Nairobi's most desirable neighborhoods
            </p>
          </div>

          <div className="flex justify-center mb-8 md:mb-12">
            <div className="inline-flex w-full max-w-md rounded-lg border border-primary-foreground/15 bg-background/10 p-1 backdrop-blur-sm">
              <button onClick={() => setFilters((current) => sanitizePropertySearchFilters({ ...current, category: "furnished" }, "rent"))} className={`flex-1 px-4 md:px-8 py-3 font-body text-xs md:text-sm font-medium tracking-wider uppercase transition-all duration-300 rounded-md ${filters.category === "furnished" ? "bg-accent text-accent-foreground" : "text-primary-foreground/70 hover:text-primary-foreground"}`}>
                Furnished ({furnishedCount})
              </button>
              <button onClick={() => setFilters((current) => sanitizePropertySearchFilters({ ...current, category: "unfurnished" }, "rent"))} className={`flex-1 px-4 md:px-8 py-3 font-body text-xs md:text-sm font-medium tracking-wider uppercase transition-all duration-300 rounded-md ${filters.category === "unfurnished" ? "bg-accent text-accent-foreground" : "text-primary-foreground/70 hover:text-primary-foreground"}`}>
                Unfurnished ({unfurnishedCount})
              </button>
            </div>
          </div>

          <div className="max-w-7xl mx-auto">
            <PropertyFilters
              value={filters}
              locations={locations}
              loadingLocations={loadingLocations}
              onChange={setFilters}
              onReset={() => setFilters(defaultRentFilters)}
              fixedListingType="rent"
            />
          </div>
        </div>

        <div className="px-4 md:px-16 pb-16">
          <div className="max-w-7xl mx-auto">
            <p className="font-body text-sm text-primary-foreground/75 mb-6">
              Showing <span className="text-primary-foreground font-semibold">{filteredProperties.length}</span> rental properties.
            </p>

            {isLoading ? (
              <p className="text-center text-primary-foreground/70 font-body py-12">Loading properties...</p>
            ) : filteredProperties.length > 0 ? (
              <div className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredProperties.map((property) => (
                  <PropertyListingCard key={property.id} {...property} statusLabel={property.status_label} categoryLabel={property.category_label} />
                ))}
              </div>
            ) : (
              <p className="text-center text-primary-foreground/70 font-body py-12">No rental properties match the selected filters.</p>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PropertiesForRentPage;
